import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { EditCriteria } from '@app/components';
import { usePermissions } from '@data/hooks';
import { Namespace } from '@data/utils/enums';
import {
  AntdCol,
  AntdInput,
  AntdRow,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  Button,
} from '@ui/components';
import { TrialsTable } from './components';
import { useQuestionTrials } from './hooks';
import './styles.css';

const { Text } = AntdTypography;

const QuestionTrials = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER, MANAGE_TRIAL } = usePermissions();

  const {
    current,
    disease,
    pageSize,
    question,
    isLoading,
    leadSponsor,
    questionType,
    diseaseOptions,
    criteriaToEdit,
    questionCriteria,
    diseasesAreLoading,
    leadSponsorsOptions,
    standardCriterionText,
    leadSponsorsAreLoading,
    isQuestionCriterionDirty,
    isQuestionCriterionUpdating,
    handleTableChange,
    handleSearchDisease,
    handleDiseasesFilter,
    handleOpenEditCriteria,
    handleCloseEditCriteria,
    handleLeadSponsorFilter,
    handleSearchLeadSponsor,
    invalidateQuestionCriteria,
    handleDiseasesSelectScroll,
    handleEditQuestionCriterion,
    handleQuestionCriterionChange,
    handleLeadSponsorsSelectScroll,
  } = useQuestionTrials();

  return (
    <>
      <div className="pt-2 px-4 pb-8">
        <AntdRow gutter={24}>
          <AntdCol lg={12}>
            <Text>{t('question_page.trials.question')}</Text>
            <AntdInput value={question?.text} disabled className="mt-2" />
          </AntdCol>
          <AntdCol lg={10} offset={2} className="relative">
            <Text>{t('question_page.trials.standard_criterion')}</Text>
            <AntdInput
              className="mt-2 z-10"
              value={standardCriterionText}
              onChange={handleQuestionCriterionChange}
              disabled={IS_REVIEWER}
              data-testid="standard-criterion-input"
            />
            <Button
              type="primary"
              className={cx(
                'absolute top-full right-3 mt-1',
                isQuestionCriterionDirty ? 'slide-down' : 'slide-up',
              )}
              loading={isQuestionCriterionUpdating}
              onClick={handleEditQuestionCriterion}
              data-testid="standard-criterion-save-button"
            >
              {t('question_page.trials.save')}
            </Button>
          </AntdCol>
        </AntdRow>

        <AntdRow gutter={24} className="mt-4">
          <AntdCol span={12} md={12} lg={8}>
            <AntdSpace orientation="vertical" className="w-full">
              <Text className="text-xs text-base-600">{t('question_page.trials.disease')}</Text>
              <AntdSelect
                showSearch
                allowClear
                onPopupScroll={handleDiseasesSelectScroll}
                loading={diseasesAreLoading}
                options={diseaseOptions}
                className="w-full"
                onSearch={handleSearchDisease}
                onSelect={handleDiseasesFilter}
                onClear={() => {
                  handleDiseasesFilter();
                  handleSearchDisease();
                }}
                value={disease}
                filterOption={false}
              />
            </AntdSpace>
          </AntdCol>
          <AntdCol span={12} md={12} lg={8}>
            <AntdSpace orientation="vertical" className="w-full">
              <Text className="text-xs text-base-600">
                {t('question_page.trials.lead_sponsor')}
              </Text>

              <AntdSelect
                showSearch
                allowClear
                onPopupScroll={handleLeadSponsorsSelectScroll}
                loading={leadSponsorsAreLoading}
                options={leadSponsorsOptions}
                className="w-full"
                onSearch={handleSearchLeadSponsor}
                onSelect={handleLeadSponsorFilter}
                onClear={() => {
                  handleLeadSponsorFilter();
                  handleSearchLeadSponsor();
                }}
                value={leadSponsor}
                filterOption={false}
              />
            </AntdSpace>
          </AntdCol>
        </AntdRow>

        <TrialsTable
          current={current}
          pageSize={pageSize}
          isLoading={isLoading}
          questionType={questionType!}
          criterias={questionCriteria?.items}
          total={questionCriteria?.count || 0}
          handleTableChange={handleTableChange}
          handleOpenEditCriteria={handleOpenEditCriteria}
        />
      </div>

      {criteriaToEdit && (
        <EditCriteria
          isQuestionCriteria
          isOpen={!!criteriaToEdit}
          onClose={handleCloseEditCriteria}
          criteria={criteriaToEdit}
          isAdmin={!!MANAGE_TRIAL}
          invalidateQuestionCriteria={invalidateQuestionCriteria}
          trialId={criteriaToEdit.trialId}
        />
      )}
    </>
  );
};

export default QuestionTrials;
